
import { motion } from 'framer-motion';
import TouchSlider from '../../../components/base/TouchSlider';

const certificates = [
  {
    icon: 'ri-shield-check-line',
    name: 'ISO 9001:2015',
    description: 'System Zarządzania Jakością',
    details:
      'Międzynarodowy standard zarządzania jakością potwierdzający najwyższe standardy procesów produkcyjnych i kontroli jakości.',
  },
  {
    icon: 'ri-leaf-line',
    name: 'ISO 14001:2015',
    description: 'System Zarządzania Środowiskowego',
    details:
      'Certyfikat potwierdzający wdrożenie systemu zarządzania środowiskowego i minimalizację wpływu na środowisko naturalne.',
  },
  {
    icon: 'ri-heart-pulse-line',
    name: 'ISO 45001:2018',
    description: 'Bezpieczeństwo i Higiena Pracy',
    details:
      'Standard zarządzania bezpieczeństwem i higieną pracy zapewniający najwyższy poziom ochrony pracowników.',
  },
  {
    icon: 'ri-building-2-line',
    name: 'EN 1090-2',
    description: 'Wykonawstwo Konstrukcji Stalowych',
    details:
      'Europejska norma dotycząca wykonawstwa konstrukcji stalowych i aluminiowych — klasa wykonania EXC3.',
  },
  {
    icon: 'ri-fire-line',
    name: 'EN ISO 3834-2',
    description: 'Wymagania Jakości Spawania',
    details:
      'Pełne wymagania jakości dotyczące spawania materiałów metalowych — najwyższy poziom certyfikacji spawalniczej.',
  },
  {
    icon: 'ri-medal-line',
    name: 'EN ISO 9606-1',
    description: 'Kwalifikacje Spawaczy',
    details:
      'Certyfikacja kwalifikacji spawaczy stali — metody TIG, MIG/MAG, MMA. Pełna kwalifikacja personelu spawalniczego.',
  },
  {
    icon: 'ri-file-shield-2-line',
    name: 'EN 15085-2',
    description: 'Spawanie Pojazdów Szynowych',
    details:
      'Certyfikat uprawniający do spawania elementów i konstrukcji pojazdów szynowych — poziom certyfikacji CL1.',
  },
  {
    icon: 'ri-verified-badge-line',
    name: 'UDT / TÜV',
    description: 'Dozór Techniczny',
    details:
      'Uprawnienia Urzędu Dozoru Technicznego do wytwarzania urządzeń ciśnieniowych i zbiorników. Certyfikacja TÜV.',
  },
];

function CertCard({ cert, index }: { cert: typeof certificates[0]; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      viewport={{ once: true }}
      className="group relative bg-gradient-to-b from-[#1A1A1A] to-[#0F0F0F] border border-[#2A2A2A] p-5 sm:p-6 hover:border-[#00E5FF]/60 hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(0,229,255,0.15)] transition-all duration-500 cursor-default h-full"
    >
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00E5FF]/0 to-transparent group-hover:via-[#00E5FF] transition-all duration-500"></div>

      <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00E5FF]/5 border border-[#00E5FF]/20 mb-4 sm:mb-5 group-hover:bg-[#00E5FF]/10 group-hover:border-[#00E5FF]/50 transition-all duration-500">
        <i className={`${cert.icon} text-2xl sm:text-3xl text-[#00E5FF]`}></i>
      </div>

      <h3
        className="text-base sm:text-lg font-bold text-white mb-1"
        style={{ fontFamily: 'Rajdhani, sans-serif' }}
      >
        {cert.name}
      </h3>
      <p
        className="text-[#00E5FF] text-[9px] sm:text-xs uppercase tracking-wider mb-2 sm:mb-3"
        style={{ fontFamily: 'Orbitron, sans-serif' }}
      >
        {cert.description}
      </p>
      <p
        className="text-[#888888] text-[11px] sm:text-xs leading-relaxed group-hover:text-[#C0C7CF] transition-colors duration-300"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {cert.details}
      </p>
    </motion.div>
  );
}

export default function CertificatesSection() {
  return (
    <section className="relative py-16 sm:py-28 bg-[#0A0A0A] overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,229,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.3) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-20"
        >
          <span
            className="text-[#00E5FF] text-[10px] sm:text-sm tracking-[0.2em] sm:tracking-[0.3em] uppercase font-semibold"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Jakość i Certyfikaty
          </span>
          <h2
            className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mt-3 sm:mt-4 leading-tight"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Międzynarodowe <span className="text-[#00E5FF]">Standardy</span>
          </h2>
          <p
            className="text-[#C0C7CF] text-xs sm:text-base mt-3 sm:mt-6 max-w-3xl mx-auto px-2"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Nasze procesy produkcyjne i systemy zarządzania spełniają najwyższe
            międzynarodowe normy jakości, bezpieczeństwa i ochrony środowiska.
          </p>
        </motion.div>

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {certificates.map((cert, index) => (
            <CertCard key={index} cert={cert} index={index} />
          ))}
        </div>

        {/* Tablet — 2 columns slider */}
        <div className="hidden sm:block md:hidden">
          <div className="grid grid-cols-2 gap-4">
            {certificates.map((cert, index) => (
              <CertCard key={index} cert={cert} index={index} />
            ))}
          </div>
        </div>

        {/* Mobile — horizontal touch slider */}
        <div className="sm:hidden">
          <TouchSlider gap={12} showDots showArrows={false} itemClassName="w-[75vw]">
            {certificates.map((cert, index) => (
              <CertCard key={index} cert={cert} index={index} />
            ))}
          </TouchSlider>
        </div>

        {/* Bottom bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-10 sm:mt-16 bg-[#111111] border border-[#00E5FF]/20 p-5 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6"
        >
          <div className="flex items-center gap-3 sm:gap-4 text-center sm:text-left">
            <div className="w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center bg-[#00E5FF]/10 border border-[#00E5FF] flex-shrink-0">
              <i className="ri-award-line text-xl sm:text-2xl text-[#00E5FF]"></i>
            </div>
            <div>
              <h4
                className="text-white font-bold text-sm sm:text-base"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                Pełna dokumentacja jakościowa
              </h4>
              <p
                className="text-[#C0C7CF] text-xs sm:text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Wszystkie certyfikaty dostępne do wglądu na życzenie klienta
              </p>
            </div>
          </div>
          <a
            href="/kontakt#formularz"
            className="px-6 sm:px-8 py-3 border border-[#00E5FF] text-[#00E5FF] font-bold text-xs sm:text-sm hover:bg-[#00E5FF] hover:text-[#050505] transition-all duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto text-center"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Zapytaj o certyfikaty
          </a>
        </motion.div>
      </div>
    </section>
  );
}
